<script setup lang="ts">
const t2 = useT2({
  'Login required': 'Reikalingas prisijungimas',
  'You must agree with privacy policy': 'Turite sutikti su privatumo politika',
  On: 'Įjungta',
  Off: 'Išjungta',
})

export type SubState = 'social' | 'aggree' | 'auth' | ''

type SubmitResult = {
  response: NotVoidPrimitives
  success: boolean
}

const props = defineProps<{
  title: stringI18n
  submit: (next: boolean) => Promise<SubmitResult>
  recheck: () => void
  isSubscribed: boolean | undefined

  enabledLabel: stringI18n
  enableLabel: stringI18n
  disabledLabel: stringI18n
}>()

const mobile = useMobile()
const auth = useAuth()
const init3 = useInited('init3')
const notify = useNotify()

const data = shallowReactive({
  state: '' as SubState,
  aggree: false,
  pending: false,
})

const render = useIsRender(() => !!data.state)

const isReady = computed(() => {
  return !auth.value.isAuth || props.isSubscribed !== undefined
})

const uiOn = computed(() => {
  return props.isSubscribed === true || !!data.state
})

function reset(showSocial = false) {
  data.aggree = false
  data.state = showSocial ? 'social' : ''

  nextTick(() => props.recheck())
}

useReset(() => {
  reset()
})

async function mutate(): Promise<NotVoidPrimitives> {
  if (data.pending) return null

  if (!auth.value.isAuth) {
    data.state = 'auth'
    return actionRes({ error: t2('Login required') })
  }

  if (props.isSubscribed === undefined) {
    props.recheck()
    return null
  }

  const next = !props.isSubscribed

  if (next && !data.aggree) {
    if (data.state !== 'aggree') {
      data.state = 'aggree'
      return null
    }

    return toResponse({
      error: t2('You must agree with privacy policy'),
    })
  }

  data.pending = true

  try {
    let success = false

    const response = await notify.watch(
      props.submit(next).then((result) => {
        success = result.success
        return result.response
      })
    )

    reset(next && success)

    return response
  } catch (error) {
    reset()
    throw error
  } finally {
    data.pending = false
  }
}
</script>

<template>
  <div :ref="notify.elem" :class="[$style.cont, 'IconSwitch', uiOn && 'on']">
    <span class="_svg16">
      <LazyIconEmail v-if="init3" class="_absolute _svg16" :class="$style.icon" />
    </span>

    <div class="_col _absolute" :class="$style.label">
      <UiNotify :notify />

      <span class="_row _ctrY _gap1">
        <span class="_h4 _colorCur">{{ title }}</span>
      </span>

      <span v-if="auth.email" class="_pt02 _ls-xs _fs08 _fw-l">
        {{ auth.email }}
      </span>
    </div>

    <span class="_mla _row _pr04 _wmin120 _end">
      <IconSwitch :yes="props.isSubscribed ? enabledLabel : enableLabel" :no="disabledLabel" :class="$style.a3" />
    </span>

    <button
      class="_absolute _inset"
      :class="$style.subscribe"
      type="button"
      :disabled="data.pending || !isReady"
      :aria-busy="data.pending"
      :aria-label="props.isSubscribed ? t2('On') : t2('Off')"
      @click="mutate"
    />

    <LazySubscriptionSellerBtnModals v-if="render" v-model:aggree="data.aggree" v-model:state="data.state" :notify :mutate :reset :mobile />
  </div>
</template>

<style module>
.cont {
  composes: global(_relative);
  composes: global(_row);
  composes: global(_ctrY);
  composes: global(d_gap2);
  composes: global(m_gap14);
  composes: global(d_h40);
  composes: global(m_h44);

  composes: global(_br99);
  composes: global(_b5);

  composes: global(_pt02);
  composes: global(_pb02);
  composes: global(_pl08);
  composes: global(_pr02);

  width: 280px;

  composes: global(_c1);
  composes: global(_bg0);

  composes: global(_start);

  --a2: var(--black);
  --a2c: var(--bg);
  --h3: 30px;
}

:global(.m) .cont {
  --h3: 32px;
  --svg: 24px;
}

.subscribe {
  background: transparent;
  composes: global(_opacity0);
}

.subscribe:disabled {
  cursor: default;
}

.icon {
  composes: global(_mta);
  composes: global(_mba);
  composes: global(_mla);
  composes: global(_mra);

  inset: 0 auto 0 8px;
}

.label {
  left: 34px;
  top: 6px;
}

.cont .a3 {
  --h2: var(--h3);
}
</style>
